import type {
  RankedSearchResult,
  SearchDocument,
  SearchFacetSelection,
  SearchSort,
} from './searchTypes';

export interface FacetOption {
  value: string;
  count: number;
}

export type FacetKey = 'speaker' | 'session' | 'topic';

function valuesFor(result: RankedSearchResult, key: FacetKey): string[] {
  if (key === 'speaker') return [result.document.speaker];
  if (key === 'session') return [result.document.session];
  return result.document.themes;
}

export function facetOptions(
  results: readonly RankedSearchResult[],
  key: FacetKey,
): FacetOption[] {
  const counts = new Map<string, number>();
  for (const result of results) {
    for (const value of valuesFor(result, key)) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort(
      (first, second) =>
        second.count - first.count || first.value.localeCompare(second.value),
    );
}

export function filterResults(
  results: readonly RankedSearchResult[],
  selection: SearchFacetSelection,
): RankedSearchResult[] {
  return results.filter(
    (result) =>
      (selection.speakers.size === 0 ||
        selection.speakers.has(result.document.speaker)) &&
      (selection.sessions.size === 0 ||
        selection.sessions.has(result.document.session)) &&
      (selection.topics.size === 0 ||
        result.document.themes.some((theme) => selection.topics.has(theme))),
  );
}

export function sortResults(
  results: readonly RankedSearchResult[],
  sort: SearchSort,
  documents: readonly SearchDocument[],
): RankedSearchResult[] {
  if (sort === 'relevance') return [...results];
  const order = new Map(
    documents.map((document, index) => [document.id, index]),
  );
  return [...results].sort(
    (first, second) =>
      (order.get(first.document.id) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(second.document.id) ?? Number.MAX_SAFE_INTEGER),
  );
}
